import React, { Fragment } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CurrencyInput,
  IntegerInput,
  NumericInput,
  PercentInput,
  NumericInputProps,
  UnlimitedCurrencyInput,
} from '@/components/NumericInput';
import {
  PolicyTypeSelectDisplay,
  PolicyTypeComboboxOption,
} from '@/components/PolicyTypeSelectDisplay';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/Combobox';
import { FundingSourceForm } from '@/components/FundingSourceForm';

import { useSelector } from 'react-redux';
import { usePolicyInputFormContext } from '@/components/PolicyInputForm';

import { selectClient, selectFeatureFlags, selectSessionAdvisor } from '@client/model';

import { getApplicablePolicyTypes } from '@client/util';
import { FundingPolicyType, fundingPolicyTypeDefList, getCoupleLtcStartData } from '@shared';

const INFLATION_MESSAGES = {
  GENERAL_DISABLED:
    'Separate inflation is active. General inflation is disabled. Please fill both Face and COB inflation fields.',
  FACE_DISABLED: 'General inflation applies to all. Face inflation is disabled.',
  COB_DISABLED: 'General inflation applies to all. COB inflation is disabled.',
} as const;

type PolicyFieldKey = keyof PolicyFundingSource;

export function InsuranceFormFields() {
  const { policyState } = usePolicyInputFormContext();
  const { policyType } = policyState;
  const client = useSelector(selectClient);
  const {
    intakeSurvey: { clientHasStartedLtc },
  } = client;

  if (!policyType) {
    return <PolicyTypeField />;
  }

  const policyRelatedFields = getPolicyFormFields(policyType);

  return (
    <Fragment>
      <PolicyTypeField />
      {clientHasStartedLtc && <BenefitUtilizedToDateField />}
      {policyRelatedFields.map((Field, index) => (
        <Field key={index} />
      ))}
    </Fragment>
  );
}

const hybridPolicyFields = [
  WaitingPeriodDaysFieldGroup,
  FaceAmountField,
  MinimumGuaranteedBenefitAmountField,
  ContinuationOfBenefitsField,
  PremiumStartYearField,
  PremiumMonthlyCostField,
  PremiumIncreaseRateField,
  PremiumIncreaseYearsField,
  LimitedPayYearsField,
  LumpSumPaymentField,
  DailyMaxBenefitsFieldGroup,
  InflationProtectionField,
  InflationProtectionOnFaceAmountField,
  InflationProtectionOnCOBAmountField,
  IsSimpleInflationProtectionField,
  InflationProtectionYearsField,
  IsIndemnityPolicyPaymentField,
];

const policyTypeFormFieldMap: Partial<Record<FundingPolicyType, React.FC[]>> = {
  [FundingPolicyType.longTermCareInsurance]: [
    WaitingPeriodDaysFieldGroup,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    PremiumIncreaseYearsField,
    LimitedPayYearsField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.shortTermCareInsurance]: [
    BenefitPeriodMonthsField,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    PremiumIncreaseYearsField,
    LimitedPayYearsField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.lifeInsuranceWithRider]: [
    WaitingPeriodDaysFieldGroup,
    FaceAmountField,
    MinimumGuaranteedBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.hybridLifeInsurance]: hybridPolicyFields,
  [FundingPolicyType.annuityHybrid]: [
    WaitingPeriodDaysFieldGroup,
    TotalLTCBalanceAtAgeField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.lifeWithIndexAccount]: [
    WaitingPeriodDaysFieldGroup,
    FaceAmountAtAgeField,
    MinimumGuaranteedBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    DailyMaxBenefitsFieldGroup,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.jointHybridAnnuityBased]: [
    PrimaryPolicyHolderField,
    WaitingPeriodDaysFieldGroup,
    JointTotalLTCBalanceAtAgeField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.hybridLifeIra]: [
    WaitingPeriodDaysFieldGroup,
    FaceAmountAtAgeField,
    ContinuationOfBenefitsField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    PolicyMonthlyWithdrawalRate,
    InflationProtectionOnFaceAmountField,
    InflationProtectionOnCOBAmountField,
    IsSimpleInflationProtectionField,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.jointHybridLifeIra]: [
    WaitingPeriodDaysFieldGroup,
    PrimaryPolicyHolderField,
    JointDeathBenefitAtAgeField,
    ContinuationOfBenefitsField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    PolicyMonthlyWithdrawalRate,
    InflationProtectionOnFaceAmountField,
    InflationProtectionOnCOBAmountField,
    IsSimpleInflationProtectionField,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.fortifiedLife]: [
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    LumpSumPaymentField,
  ],
  [FundingPolicyType.nylSecureCare]: [
    WaitingPeriodDaysFieldGroup,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
  ],
  [FundingPolicyType.nylJointSecureCare]: [
    WaitingPeriodDaysFieldGroup,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
  ],
};

export const getPolicyFormFields = (policyType: FundingPolicyType): React.FC[] => {
  // If the policy type exists in policyTypeFormFieldMap, return its fields
  if (policyTypeFormFieldMap[policyType]) {
    return policyTypeFormFieldMap[policyType]!;
  }

  // Find the policy definition in fundingPolicyTypeDefList
  const policyDef = fundingPolicyTypeDefList.find(def => def.value === policyType);

  // If the policy type is missing, check for its genericType and return those fields
  if (policyDef?.genericType && policyTypeFormFieldMap[policyDef.genericType]) {
    return policyTypeFormFieldMap[policyDef.genericType]!;
  }

  // If the policy type is missing, check for its calculationType and return those fields
  if (policyDef && policyTypeFormFieldMap[policyDef.calculationType]) {
    return policyTypeFormFieldMap[policyDef.calculationType]!;
  }

  // If neither is found, return an empty array
  return [];
};

type InsuranceFormFieldProps = {
  policyFieldKey: PolicyFieldKey;
  label: string;
  InputField: typeof NumericInput;
  placeholder: string;
  unit?: string;
  convertFromDaily?: (dailyValue: number) => number;
  convertToDaily?: (inputValue: number) => number;
};

const isViewOnly = (policyType: FundingPolicyType | null) =>
  policyType === FundingPolicyType.allstateHybridPolicy ||
  policyType === FundingPolicyType.agisGroupPolicy;

function getInflationProtectionFieldStates(policyState: PolicyFundingSource) {
  const generalInflation = policyState.policyInflationProtection;
  const faceInflation = policyState.policyInflationProtectionOnFaceAmount;
  const cobInflation = policyState.policyInflationProtectionOnCOBAmount;

  const isGeneralFilled = generalInflation != null;
  const isFaceFilled = faceInflation != null;
  const isCobFilled = cobInflation != null;
  const isFaceOrCobFilled = isFaceFilled || isCobFilled;

  const general = {
    disabled: isFaceOrCobFilled,
    helperText: isFaceOrCobFilled ? INFLATION_MESSAGES.GENERAL_DISABLED : undefined,
  };

  const face = {
    disabled: isGeneralFilled,
    helperText: isGeneralFilled ? INFLATION_MESSAGES.FACE_DISABLED : undefined,
  };

  const cob = {
    disabled: isGeneralFilled,
    helperText: isGeneralFilled ? INFLATION_MESSAGES.COB_DISABLED : undefined,
  };

  return { general, face, cob };
}

function InsuranceFormNumericField({
  InputField,
  placeholder,
  label,
  policyFieldKey,
  convertFromDaily,
  convertToDaily,
  inputProps = {},
}: InsuranceFormFieldProps & { inputProps?: Partial<NumericInputProps> }) {
  const { formErrors, policyState, handleChangePolicyField } = usePolicyInputFormContext();
  const disabled = isViewOnly(policyState.policyType);

  const fieldValue = policyState[policyFieldKey];
  const numericValue = typeof fieldValue === 'number' ? fieldValue : null;
  const displayValue =
    convertFromDaily && numericValue ? convertFromDaily(numericValue) : numericValue;

  function handleChange(value: number | null) {
    const parse =
      convertToDaily && value
        ? (nextValue: PolicyFieldValue) => {
            return typeof nextValue === 'number' && nextValue !== null
              ? convertToDaily(nextValue)
              : nextValue;
          }
        : undefined;

    handleChangePolicyField(policyFieldKey, parse)(value);
  }

  return (
    <FundingSourceForm.NumericField
      fieldError={formErrors[policyFieldKey]}
      InputField={InputField}
      inputProps={{
        label,
        disabled,
        placeholder,
        value: displayValue,
        onChange: handleChange,
        onPreview: handleChange,
        ...inputProps,
      }}
    />
  );
}

type InsuranceFormFieldContainerProps = {
  title: string | React.ReactNode;
  description: string;
  FieldComponent: React.FC;
  example?: string;
  children: React.ReactNode;
  className?: string;
};

function InsuranceFormFieldContainer({
  title,
  description,
  FieldComponent,
  children,
  example,
  className,
}: InsuranceFormFieldContainerProps) {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  const policyFields = policyType ? getPolicyFormFields(policyType) : [];
  const isLastField = policyFields[policyFields.length - 1] === FieldComponent;

  return (
    <FundingSourceForm.FieldContainer
      title={title}
      description={description}
      example={example}
      isLastField={isLastField}
      className={className}
    >
      {children}
    </FundingSourceForm.FieldContainer>
  );
}

function WaitingPeriodDaysFieldGroup() {
  const {
    intakeSurvey: { clientHasStartedLtc },
  } = useSelector(selectClient);
  return (
    <InsuranceFormFieldContainer
      title={
        clientHasStartedLtc ? 'Remaining Waiting/Elimination Period' : 'Waiting/Elimination Period'
      }
      description={
        clientHasStartedLtc
          ? `Number of days remaining in the elimination period before the policy begins to pay for services.`
          : `Number of days you receive care before the policy
                              begins to pay for services.`
      }
      FieldComponent={WaitingPeriodDaysFieldGroup}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter days"
        label="HOME CARE WAITING PERIOD"
        policyFieldKey="homeCareWaitingPeriodDays"
      />
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter days"
        label="FACILITY CARE WAITING PERIOD"
        policyFieldKey="facilityCareWaitingPeriodDays"
      />
    </InsuranceFormFieldContainer>
  );
}

function PremiumStartYearField() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  const title =
    policyType === FundingPolicyType.fortifiedLife
      ? 'Membership Start Year '
      : 'Policy Purchase Year';
  const description =
    policyType === FundingPolicyType.fortifiedLife
      ? 'The calendar year when the membership was purchased.'
      : 'The calendar year when the policy was purchased.';

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={PremiumStartYearField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter year"
        label={title.toUpperCase()}
        policyFieldKey="policyPremiumStartYear"
      />
    </InsuranceFormFieldContainer>
  );
}

function PremiumMonthlyCostField() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  const title =
    policyType === FundingPolicyType.fortifiedLife ? 'Monthly Maintenance Fees' : 'Monthly Premium';
  const description =
    policyType === FundingPolicyType.fortifiedLife
      ? 'The amount paid monthly to participate in the program.'
      : 'The amount paid for the policy every month.';

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={PremiumMonthlyCostField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label={title.toUpperCase()}
        policyFieldKey="policyPremiumMonthlyCost"
      />
    </InsuranceFormFieldContainer>
  );
}

function LimitedPayYearsField() {
  return (
    <InsuranceFormFieldContainer
      title="Limited Pay Period"
      description="Number of years premium payments are required for limited pay policies. Leave blank if N/A."
      FieldComponent={LimitedPayYearsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter years"
        label="LIMITED PAY YEARS"
        policyFieldKey="policyLimitedPayYears"
        unit="years"
      />
    </InsuranceFormFieldContainer>
  );
}

function LumpSumPaymentField() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  const title =
    policyType === FundingPolicyType.fortifiedLife ? 'Entrance Fee' : 'Lump-Sum Payment';
  const description =
    policyType === FundingPolicyType.fortifiedLife
      ? 'Fee required to enter the program. Provide the total amount, not the installment amount.'
      : 'A one-time premium payment made towards the policy. Leave blank if not applicable.';

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={LumpSumPaymentField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label={title.toUpperCase()}
        policyFieldKey="policyLumpSumPayment"
      />
    </InsuranceFormFieldContainer>
  );
}

function IsIndemnityPolicyPaymentField() {
  const { policyState, handleChangePolicyField } = usePolicyInputFormContext();
  const disabled = isViewOnly(policyState.policyType);

  return (
    <InsuranceFormFieldContainer
      title="Payment Type"
      description="The type of payment the policy provides."
      FieldComponent={IsIndemnityPolicyPaymentField}
    >
      <FundingSourceForm.BooleanToggleField
        changeHandler={handleChangePolicyField('isIndemnityPolicyPayment')}
        disabled={disabled}
        value={policyState.isIndemnityPolicyPayment}
        enabledLabel="Indemnity"
        disabledLabel="Reimbursement"
      />
    </InsuranceFormFieldContainer>
  );
}

function MaximumBenefitAmountField() {
  return (
    <InsuranceFormFieldContainer
      title="Max Policy Benefit"
      description={`This is the maximum dollar amount that will be
                    paid out for long-term care under this policy.
                    Refer to your policy for how your Max Policy
                    Benefit is calculated. For most policies, you
                    multiply your daily benefit amount by the total
                    number of days in the benefit period. The amount
                    will be inflated by the chosen inflation
                    protection`}
      example={`Example: For a 5-year benefit period with a $200
                daily benefit, the maximum benefit would be 5 x
                365 x 200 = $365,000.`}
      FieldComponent={MaximumBenefitAmountField}
    >
      <InsuranceFormNumericField
        InputField={UnlimitedCurrencyInput}
        placeholder="Unlimited"
        label="MAX BENEFIT AMOUNT"
        policyFieldKey="policyMaximumBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

function TotalLTCBalanceAtAgeField() {
  const {
    clientFirstName,
    intakeSurvey: { clientHasStartedLtc },
    appliedInferenceSet: { ltcAtAge },
  } = useSelector(selectClient);
  return (
    <InsuranceFormFieldContainer
      title={`Total LTC Balance at Age ${ltcAtAge}`}
      description={`The expected or guaranteed accumulated value at
                    age ${ltcAtAge}, which is ${clientFirstName}'s
                    ${clientHasStartedLtc ? '' : 'expected  '}start age for long-term care (LTC). This
                    balance is the total amount available to cover
                    qualified LTC expenses.`}
      FieldComponent={TotalLTCBalanceAtAgeField}
    >
      <InsuranceFormNumericField
        InputField={UnlimitedCurrencyInput}
        placeholder="Unlimited"
        label="TOTAL LTC BALANCE"
        policyFieldKey="policyMaximumBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

type JointBalanceAtAgeFieldProps = {
  policyFieldKey: PolicyFieldKey;
  label: string;
  title: string;
  description: string;
};

function JointBalanceAtAgeField({
  policyFieldKey,
  label,
  title,
  description,
}: JointBalanceAtAgeFieldProps) {
  const client = useSelector(selectClient);
  const { policyState } = usePolicyInputFormContext();
  const { mutableClientPartner, clientFirstName } = client;
  const coupleLtcStartData = getCoupleLtcStartData(client, mutableClientPartner);
  const expectedPolicyHolderAgeAtPolicyActivation = coupleLtcStartData
    ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(policyState.isPrimaryPolicyHolder)
    : null;

  const primaryPolicyHolderName = policyState.isPrimaryPolicyHolder
    ? clientFirstName
    : mutableClientPartner?.clientFirstName;
  const startsLtcFirstPartnerName = coupleLtcStartData?.clientStartsLtcFirst
    ? clientFirstName
    : mutableClientPartner?.clientFirstName;

  return (
    <InsuranceFormFieldContainer
      title={`${title} at Age ${expectedPolicyHolderAgeAtPolicyActivation}`}
      description={description}
      example={`The policy activation age ${expectedPolicyHolderAgeAtPolicyActivation} is determined by two factors.
                1) the primary policy holder, and 2) the
                age at which the first partner is expected to
                start LTC. Since ${primaryPolicyHolderName} is the primary policy holder, the policy activation age is based on ${primaryPolicyHolderName}'s age when the first partner (${startsLtcFirstPartnerName}) is expected to start LTC in year ${coupleLtcStartData?.coupleLtcStartYear}.`}
      FieldComponent={JointTotalLTCBalanceAtAgeField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label={label}
        policyFieldKey={policyFieldKey}
      />
    </InsuranceFormFieldContainer>
  );
}

function JointTotalLTCBalanceAtAgeField() {
  return (
    <JointBalanceAtAgeField
      policyFieldKey="policyMaximumBenefitAmount"
      label="TOTAL LTC BALANCE"
      title="Total LTC Balance"
      description="This balance is the expected or guaranteed
                    accumulated value when the first partner is
                    expected to begin long-term care."
    />
  );
}

function JointDeathBenefitAtAgeField() {
  return (
    <JointBalanceAtAgeField
      policyFieldKey="policyMaximumBenefitAmount"
      label="DEATH BENEFIT"
      title="Face Amount (Death Benefit)"
      description={`The death benefit at the time of the first partner's
                   long-term care start age based on
                   projected growth rates. Unused funds are paid to
                   beneficiaries upon policyholder's death.`}
    />
  );
}

function FaceAmountField() {
  return (
    <InsuranceFormFieldContainer
      title="Face Amount (Death Benefit)"
      description={`This pool is used for qualified LTC expenses. Unused funds are paid to beneficiaries upon policyholder's death. The amount will be inflated by the chosen inflation protection.`}
      FieldComponent={FaceAmountField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label="FACE AMOUNT"
        policyFieldKey="policyMaximumBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

function FaceAmountAtAgeField() {
  const {
    appliedInferenceSet: { ltcAtAge },
  } = useSelector(selectClient);
  return (
    <InsuranceFormFieldContainer
      title={`Face Amount (Death Benefit) at Age ${ltcAtAge}`}
      description={`The death benefit at age ${ltcAtAge} based on
                  projected growth rates. Unused funds are paid to
                    beneficiaries upon policyholder's death.`}
      FieldComponent={FaceAmountAtAgeField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label="FACE AMOUNT"
        policyFieldKey="policyMaximumBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

function ContinuationOfBenefitsField() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();
  let title;
  let label;
  switch (policyType) {
    case FundingPolicyType.nylAssetFlex:
      title = 'Extension of Benefits Amount';
      label = 'EOB AMOUNT';
      break;
    default:
      title = 'Continuation of Benefits Amount';
      label = 'COB AMOUNT';
      break;
  }

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={`This pool is utilized for qualified long-term
                    care expenses after the Face Amount is depleted.
                    Any funds remaining in this pool will not be
                    paid to beneficiaries. The amount will be
                    inflated by the chosen inflation protection.`}
      FieldComponent={ContinuationOfBenefitsField}
    >
      <InsuranceFormNumericField
        InputField={UnlimitedCurrencyInput}
        placeholder="Unlimited"
        label={label}
        policyFieldKey="policyContinuationBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

function BenefitPeriodMonthsField() {
  return (
    <InsuranceFormFieldContainer
      title="Benefit Period Months"
      description="The maximum length of time your insurance provider will payout benefits. "
      FieldComponent={BenefitPeriodMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter months"
        label="BENEFIT PERIOD MONTHS"
        policyFieldKey="policyBenefitPeriodMonths"
        unit="months"
      />
    </InsuranceFormFieldContainer>
  );
}

function MinimumGuaranteedBenefitAmountField() {
  return (
    <InsuranceFormFieldContainer
      title="Minimum Guaranteed Death Benefit"
      description="The minimum (residual) amount that will be paid to
                  beneficiaries upon the policyholder's death
                  regardless of the amount used for LTC expenses.
                  The amount will not be affected by the chosen
                  inflation protection."
      FieldComponent={MinimumGuaranteedBenefitAmountField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label="MINIMUM DEATH BENEFIT"
        policyFieldKey="policyMinimumGuaranteedBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

export function BenefitUtilizedToDateField() {
  return (
    <InsuranceFormFieldContainer
      title="Benefit Utilized To Date"
      description="The dollar amount of benefits used to date to pay for long-term care."
      FieldComponent={BenefitUtilizedToDateField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label="BENEFIT UTILIZED TO DATE"
        policyFieldKey="policyBenefitUtilizedToDate"
      />
    </InsuranceFormFieldContainer>
  );
}

export function PolicyMonthlyWithdrawalRate() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  let title;
  let description = `The amount limit the policy will cover for each day of service based on the care setting. The amounts will be inflated by the chosen inflation protection.`;

  switch (policyType) {
    case FundingPolicyType.hybridLifeIra:
    case FundingPolicyType.jointHybridLifeIra:
      title = `Monthly Withdrawal Rate for Base Policy`;
      description = `The percentage of the benefit amount that can be withdrawn per month while withdrawing from the base policy.`;
      break;
  }
  return (
    <InsuranceFormFieldContainer
      title={title ?? 'Monthly Withdrawal Rate'}
      description={
        description ?? 'The percentage of the benefit amount that can be withdrawn per month.'
      }
      FieldComponent={PolicyMonthlyWithdrawalRate}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="MONTHLY WITHDRAWAL RATE"
        policyFieldKey="policyMonthlyWithdrawalRate"
      />
    </InsuranceFormFieldContainer>
  );
}

function PremiumIncreaseYearsField() {
  return (
    <InsuranceFormFieldContainer
      title="Premium Increase Period"
      description="Number of years the premium will increase. If left blank, the premiums will increase until projected on-claim year."
      FieldComponent={BenefitPeriodMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter years"
        label="PREMIUM INCREASE YEARS"
        policyFieldKey="policyPremiumIncreaseYears"
        unit="years"
      />
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionYearsField() {
  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection Period"
      description="Number of years the value of benefits will increase. If left blank, the benefits will increase until projected end of care year."
      FieldComponent={BenefitPeriodMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter years"
        label="INFLATION PROTECTION PERIOD"
        policyFieldKey="policyInflationProtectionYears"
        unit="years"
      />
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionField() {
  const { policyState } = usePolicyInputFormContext();
  const { general } = getInflationProtectionFieldStates(policyState);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection"
      description="The percentage that all benefit amounts increase to keep up with inflation"
      FieldComponent={InflationProtectionField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION"
        policyFieldKey="policyInflationProtection"
        inputProps={{
          disabled: general.disabled,
        }}
      />
      {general.helperText && (
        <div className="pt-[20px] text-xs text-gray-400">{general.helperText}</div>
      )}
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionOnFaceAmountField() {
  const { policyState } = usePolicyInputFormContext();
  const { face } = getInflationProtectionFieldStates(policyState);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection on Face Amount"
      description="The percentage the face amount increases to keep up with inflation"
      FieldComponent={InflationProtectionOnFaceAmountField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION (FACE AMOUNT)"
        policyFieldKey="policyInflationProtectionOnFaceAmount"
        inputProps={{
          disabled: face.disabled,
        }}
      />
      {face.helperText && <div className="pt-[20px] text-xs text-gray-400">{face.helperText}</div>}
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionOnCOBAmountField() {
  const { policyState } = usePolicyInputFormContext();
  const { cob } = getInflationProtectionFieldStates(policyState);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection on COB Amount"
      description="The percentage the COB amount increases to keep up with inflation"
      FieldComponent={InflationProtectionOnCOBAmountField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION (COB AMOUNT)"
        policyFieldKey="policyInflationProtectionOnCOBAmount"
        inputProps={{
          disabled: cob.disabled,
        }}
      />
      {cob.helperText && <div className="pt-[20px] text-xs text-gray-400">{cob.helperText}</div>}
    </InsuranceFormFieldContainer>
  );
}

const MAX_BENEFIT_INTERVAL = ['daily', 'monthly', 'annual'];

function DailyMaxBenefitsFieldGroup() {
  const client = useSelector(selectClient);
  const {
    appliedInferenceSet: { ltcAtAge },
    mutableClientPartner,
    clientFirstName,
  } = client;
  const {
    policyState: { policyType },
    policyState,
    maxBenefitsInterval,
    handleMaxBenefitsIntervalChange,
  } = usePolicyInputFormContext();

  function getConvertFromDaily() {
    if (maxBenefitsInterval === 'daily') {
      return undefined;
    }

    return (daily: number) => convertFromDailyEquivalent(daily, maxBenefitsInterval);
  }

  function getConvertToDaily() {
    if (maxBenefitsInterval === 'daily') {
      return undefined;
    }

    return (input: number) => convertToDailyEquivalent(input, maxBenefitsInterval);
  }

  let title;
  let description = `The amount limit the policy will cover per selected interval based on the care setting. The amounts will be inflated by the chosen inflation protection.`;

  switch (policyType) {
    case FundingPolicyType.annuityHybrid:
      title = `Max Benefits at Age ${ltcAtAge}`;
      break;
    case FundingPolicyType.lifeWithIndexAccount:
      title = `Max Benefits at Age ${ltcAtAge}`;
      description = `The amount limit the policy will cover per selected interval based on the care setting. This amount is based on the death benefit when ${clientFirstName} is ${ltcAtAge} and the withdrawl rate.`;
      break;
    case FundingPolicyType.jointHybridAnnuityBased: {
      const coupleLtcStartData = getCoupleLtcStartData(client, mutableClientPartner);
      const expectedPolicyHolderAgeAtPolicyActivation = coupleLtcStartData
        ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(policyState.isPrimaryPolicyHolder)
        : null;
      title = `Max Benefits at Age ${expectedPolicyHolderAgeAtPolicyActivation}`;
      description = `The amount limit the policy will cover per selected interval based on the care setting. See the "Total LTC Balance" section for details on the policy activation age.`;
      break;
    }
    case FundingPolicyType.jointHybridLifeIra:
    case FundingPolicyType.hybridLifeIra:
      title = `Max Benefits for COB Rider`;
      description = `The initial monthly benefit amount for the COB rider. This is not the monthly amount for the base policy.`;
      break;
  }

  return (
    <InsuranceFormFieldContainer
      title={
        <div className="flex items-center gap-2">
          <span>{title ?? 'Max Benefits'}</span>
          <Select
            value={maxBenefitsInterval}
            onValueChange={handleMaxBenefitsIntervalChange}
          >
            <SelectTrigger className="h-9 w-max rounded-lg border-gray-200 bg-gray-100 text-sm capitalize text-gray-800">
              <SelectValue
                className="text-sm text-gray-600"
                placeholder="Select One"
              />
            </SelectTrigger>
            <SelectContent
              className="max-h-[200px] overflow-y-auto"
              position="popper"
              sideOffset={4}
            >
              {MAX_BENEFIT_INTERVAL.map(interval => (
                <SelectItem
                  key={interval}
                  value={interval}
                  className="py-2 capitalize"
                >
                  {interval}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
      description={description}
      example="Use the unit selector above to enter your benefit amount in the format shown in your policy."
      FieldComponent={DailyMaxBenefitsFieldGroup}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`HOME CARE (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyHomeBenefitAmount"
        convertFromDaily={getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
      />
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`INDEPENDENT LIVING (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyIndependentLivingBenefitAmount"
        convertFromDaily={getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
      />
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`ASSISTED LIVING (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyAssistedLivingBenefitAmount"
        convertFromDaily={getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
      />
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`NURSING HOME (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyNursingHomeBenefitAmount"
        convertFromDaily={getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
      />
    </InsuranceFormFieldContainer>
  );
}

function IsSimpleInflationProtectionField() {
  const { policyState, handleChangePolicyField } = usePolicyInputFormContext();
  const disabled = isViewOnly(policyState.policyType);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection Type"
      description="Default calculation is compound."
      FieldComponent={IsSimpleInflationProtectionField}
    >
      <FundingSourceForm.BooleanToggleField
        changeHandler={handleChangePolicyField('isSimpleInflationProtection')}
        value={policyState.isSimpleInflationProtection}
        enabledLabel="Simple"
        disabledLabel="Compound"
        disabled={disabled}
      />
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionFieldGroup() {
  const { policyState, handleChangePolicyField } = usePolicyInputFormContext();
  const disabled = isViewOnly(policyState.policyType);
  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection"
      description="The percentage the value of benefits increases to keep up with inflation. Default calculation is compound."
      FieldComponent={InflationProtectionFieldGroup}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION"
        policyFieldKey="policyInflationProtection"
      />
      <div>
        <FundingSourceForm.BooleanToggleField
          changeHandler={handleChangePolicyField('isSimpleInflationProtection')}
          value={policyState.isSimpleInflationProtection}
          enabledLabel="Simple"
          disabledLabel="Compound"
          disabled={disabled}
        />
      </div>
    </InsuranceFormFieldContainer>
  );
}

function PrimaryPolicyHolderField() {
  const { policyState, handleChangePolicyField } = usePolicyInputFormContext();
  const { mutableClientPartner, clientFullName } = useSelector(selectClient);
  const disabled = isViewOnly(policyState.policyType);
  return (
    <InsuranceFormFieldContainer
      title="Primary Policy Holder"
      description={`${clientFullName} is the assumed primary policyholder. If this is incorrect, please deselect the toggle below.`}
      FieldComponent={PrimaryPolicyHolderField}
    >
      <FundingSourceForm.BooleanToggleField
        changeHandler={handleChangePolicyField('isPrimaryPolicyHolder')}
        value={policyState.isPrimaryPolicyHolder ?? false}
        enabledLabel={clientFullName}
        disabledLabel={mutableClientPartner?.clientFullName || 'Partner'}
        disabled={disabled}
      />
    </InsuranceFormFieldContainer>
  );
}

function PremiumIncreaseRateField() {
  const { policyState } = usePolicyInputFormContext();
  const policyTypeIsFortifiedLife = policyState.policyType === FundingPolicyType.fortifiedLife;

  let title = 'Premium Increase Rate';
  let description = 'The annual percentage increase in the premium amount.';
  if (policyTypeIsFortifiedLife) {
    title = 'Maintenance Fee Increase Rate';
    description = 'The annual percentage increase in the monthly maintenance fee.';
  }

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={LimitedPayYearsField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="ANNUAL INCREASE RATE"
        policyFieldKey="policyPremiumIncreaseRate"
      />
    </InsuranceFormFieldContainer>
  );
}

export function PolicyTypeField() {
  const { policyState, handleChangePolicyField } = usePolicyInputFormContext();
  const { policyType } = policyState;
  const client = useSelector(selectClient);
  const featureFlags = useSelector(selectFeatureFlags);
  const applicablePolicyTypes = getApplicablePolicyTypes(client, featureFlags);

  // Create the options array from applicable policy types
  const allPolicyTypeOptions = applicablePolicyTypes.map(policyTypeDef => ({
    value: policyTypeDef.value,
    label: `${policyTypeDef.carrier ? `${policyTypeDef.carrier} ` : ''}${policyTypeDef.label}`,
    selectedDisplay: <PolicyTypeSelectDisplay policyType={policyTypeDef} />,
    optionDisplay: <PolicyTypeComboboxOption policyType={policyTypeDef} />,
  }));

  // Create a new array with the selected policy type first (if it exists)
  const policyTypeOptions = policyType
    ? [
        ...allPolicyTypeOptions.filter(option => option.value === policyType),
        ...allPolicyTypeOptions.filter(option => option.value !== policyType),
      ]
    : allPolicyTypeOptions;

  const { advisorId, advisorEmail, organizationDisplayName } = useSelector(selectSessionAdvisor);
  const requestFormBaseUrl = 'https://waterlily.typeform.com/to/YSsEMouO';
  const requestFormUrl = `${requestFormBaseUrl}#advisor_id=${encodeURIComponent(advisorId)}&advisor_email=${encodeURIComponent(advisorEmail)}&advisor_organization=${organizationDisplayName ? encodeURIComponent(organizationDisplayName) : ''}`;

  const disabled = isViewOnly(policyState.policyType);

  return (
    <InsuranceFormFieldContainer
      title="Policy Type"
      description="Your specific long-term care policy or a generic type"
      FieldComponent={PolicyTypeField}
      className="md:col-start-1 md:col-end-4"
    >
      <Combobox
        options={policyTypeOptions}
        value={policyState.policyType}
        onChange={handleChangePolicyField('policyType')}
        className="relative z-[1000] w-full"
        disabled={disabled}
        emptyMessage={
          <div className="flex flex-col gap-1 p-4">
            <a
              href={requestFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button size="sm">Request Policy Type</Button>
            </a>
          </div>
        }
      />
    </InsuranceFormFieldContainer>
  );
}

function convertToDailyEquivalent(value: number, interval: string): number {
  switch (interval) {
    case 'monthly':
      return value / 30;
    case 'annual':
      return value / 365;
    default:
      return value;
  }
}

function convertFromDailyEquivalent(dailyValue: number, interval: string): number {
  switch (interval) {
    case 'monthly':
      return dailyValue * 30;
    case 'annual':
      return dailyValue * 365;
    default:
      return dailyValue;
  }
}
